import { ThemeConfig } from "@/plugins/config";

export enum ErrorType {
  "E400" = 400,
  "E401" = 401,
  "E420" = 420,
  "E428" = 428,
  "E500" = 500,
  "E504" = 504,
  "ENET" = "Connection",
  "ETIMEDOUT" = "ETIMEDOUT",
}

export interface IErrorDataMessage {
  showCancel: boolean;
  closeButtonLabel: string;
  modalClasses: string[];
  message: string;
  onCloseAction: (err: Error) => void;
  type: ErrorType;
}

export interface RootState {
  appName: string;
  appVersion: string;
  theme: ThemeConfig;
  isOnline: boolean;
}

/*
 * Types from BASICS are used across all of the modules on CERT: Students App
 * It is normal to see types that are related to specific modules, such as Auth or Accounts
 * The main goal is to decrease the size of each function by not adding entire modules when it is not needed
 */

export interface IBasicsOrg {
  orgid: number;
  channel_id: number;
  parent_id: number;
  grade12_allyear: 1 | 0;
  single_test: 1 | 0;
  mq_middle_access: 1 | 0;
  mq_high_access: 1 | 0;
}

export interface IBasicsAuthToken {
  uid: number;
  name: string;
  username: string;
  usertype: "student" | "parent" | "admin";
  grade: number;
  grade_type: "high" | "middle";
  edition_id: number;
  uproduct_id: number;
  permissions: IBasicsAuthPermissions;
  iat: number;
  exp: number;
}

export interface IBasicsAssessmentPeriods {
  id: number;
  key: BasicsWindows;
  starts_at: string;
  ends_at: string;
  channel_id: number;
  orgid: number;
  custom: 0 | 1;
}

export interface IBasicsAuthPermissions {
  homeRoom: 1 | 0;
  examRoom: 1 | 0;
  studyHall: 1 | 0;
  quizzes: 1 | 0;
  courseReport: 1 | 0;
  readinessReport: 1 | 0;
  scoreProgressionReport: 1 | 0;
  onlineTesting: 1 | 0;
  offlineEntry: "student" | "admin" | null;
  dashboard: 1 | 0;
  periods: IBasicsAssessmentPeriods[];
  orgPeriods: number[];
}

export interface IBasicsUid {
  id: number;
}

export interface IBasicsAccountLogin {
  uid: number;
  reference_date: number;
  timestamp: number;
  navigation_data: any;
}

export interface IBasicsAccountProduct {
  id: number;
  uid: number;
  edition_id: number;
  edition_sku: string;
  dt_expire: string;
  locked: 0 | 1;
}

export interface IBasicsSectionParams {
  subject: BasicsSubjects;
  test_num: number;
}

export interface IBasicsSectionRedux extends IBasicsSectionParams {
  score: number;
  feedback_score: number;
}

export interface IBasicsTestSectionParams extends IBasicsSectionParams {
  grade: number;
  periods: IBasicsAssessmentPeriods[];
}

export interface IBasicsFriendlyError {
  code: number;
  message: string;
}

export interface IBasicsScoreRanges {
  english: number;
  math: number;
  reading: number;
  science: number;
}

export interface IBasicsScoreLimits {
  min: number;
  max: number;
}

export interface IBasicsHtmlResponse {
  statusCode: number;
  headers: { [index: string]: any };
  body: string;
}

export enum BasicsPeriod {
  fall = 1,
  winter = 2,
  spring = 3,
}

export enum BasicsWindows {
  fall = "fall",
  winter = "winter",
  spring = "spring",
}

export enum BasicsTypes {
  high = "high",
  middle = "middle",
}

export enum BasicsSubjects {
  ELA = "ELA",
  Math = "Math",
  Reading = "Reading",
}

export enum BasicsExams {
  explore = "explore",
  preact = "preact",
  act = "act",
}

export interface Answers {
  [key: number]: IAnswer;
}

// Core
export interface IUSection {
  answers: Answers;
  id: number;
  uid: number;
  section_id: number;
  test_num: number;
  number: number;
  title: string;
  title_test: string;
  ct: number;
  exam: "act";
  time: number;
  time_remain: number;
  subject: BasicsSubjects;
  status: SectionStatus;
  is_completed: 0 | 1;
  is_started: 0 | 1;
  pct_review: number;
  dt_completed: string;
  i: number;
  c: number;
  s: number;
  correct: string | null;
  correct_obj: Map<number, number> | null;
}

export interface IUSectionReview {
  id: number;
  section_id: number;
  dt_start_review: string;
  dt_reviewed: string;
  pct_review: number;
  ct_review: number;
  pct_vid_all: number;
  status: SectionStatus;
}

export interface IRawAnswer {
  usection_id: number;
  question_id: number;
  question_num: number;
  time_spent: number;
  time_remain: number;
  answer: string | null;
  answer_object: Map<number, number> | null;
}

export interface IAnswer {
  usection_id: number;
  question_id: number;
  question_num: number;
  time_spent: number;
  time_remain: number;
  answer: string | null;
  answer_obj: any;
  ics: "i" | "c" | "s" | "";
  flagged: boolean;
}

export interface IAnswerInsert {
  usection_id: number;
  uid: number;
  question_id: number;
  answer: string | null;
  answer_obj: Map<number, number> | null;
  time: number;
  is_c: 0 | 1;
  is_i: 0 | 1;
  is_s: 0 | 1;
  ics: "i" | "c" | "s";
  created_at: string;
  updated_at: string;
}

export interface ILesson {
  id: number;
  name: string;
  subject: BasicsSubjects;
}

export enum SectionStatus {
  Graded = "Graded",
  InReview = "In Review",
  Reviewed = "Reviewed",
}

export interface IChoices {
  A: string;
  B: string;
  C: string;
  D: string;
  F: string;
  G: string;
  H: string;
  J: string;
}
